import {
  compact, find, get, isString, join, map, uniq, uniqBy, some,
} from 'lodash';
import { format, zonedTimeToUtc } from 'date-fns-tz';
import formatDate, { DateTypes } from '@/utils/format-date';

export const SIDEBAR_OPTIONS = {
  COLUMNS: 1,
  FILTERS: 2,
  HIGHLIGHTS: 3,
};

const getConnectionItems = (connection) => {
  if (connection?.nodes) return connection.nodes;
  if (connection?.edges) return map(connection.edges, 'node');
  return connection || [];
};

const getSpecialMarketNames = (specialMarket, key, nameKey) => uniq(compact([
  ...map(getConnectionItems(specialMarket?.[key]), (item) => (
    isString(item) ? item : get(item, nameKey)
  )),
  ...getConnectionItems(get(specialMarket, `${key.slice(0, -1)}Names`)),
]));

const getSpecialMarket = (betPart) => (
  betPart?.specialMarket
  || betPart?.huddleContentInfo?.specialMarket
  || betPart?.huddleContentInfo?.market?.specialMarket
);

const getBetPartSelection = (betPart) => (
  betPart?.huddleContentInfo?.specialSelection
  || betPart?.huddleContentInfo?.selection
  || betPart?.selection
);

const formatEventName = (event, isUsaView = false) => {
  if (!event?.eventName) return '';

  const [home, away] = event.eventName.split(' v ');
  if (!away) return home;
  return isUsaView ? `${away} @ ${home}` : `${home} - ${away}`;
};

const getSpecialMarketEvents = (betPart, specialMarket) => {
  const socketEvents = betPart?.huddleContentInfo?.specialMarketEvents;
  if (socketEvents?.length) return socketEvents;
  return getConnectionItems(specialMarket?.events);
};

const getSpecialMarketCompetitions = (betPart, specialMarket) => {
  const socketCompetitions = betPart?.huddleContentInfo?.specialMarketCompetitions;
  if (socketCompetitions?.length) return socketCompetitions;
  return getConnectionItems(specialMarket?.competitions);
};

const getSpecialMarketSports = (betPart, specialMarket) => {
  const socketSports = betPart?.huddleContentInfo?.specialMarketSports;
  if (socketSports?.length) return socketSports;
  return getConnectionItems(specialMarket?.sports);
};

const getSpecialMarketEventName = (betPart, specialMarket) => {
  const eventNames = compact(map(
    getSpecialMarketEvents(betPart, specialMarket),
    (event) => formatEventName(event, event?.isUSAView || event?.isUsaView),
  ));
  if (eventNames.length) {
    return eventNames.length === 1 ? eventNames[0] : join(eventNames, ', ');
  }
  const fallbackNames = getSpecialMarketNames(specialMarket, 'events', 'eventName');
  if (!fallbackNames.length) return null;
  return fallbackNames.length === 1 ? fallbackNames[0] : join(fallbackNames, ', ');
};

const getSpecialMarketCompetitionNames = (betPart, specialMarket) => {
  const competitionNames = compact(map(
    getSpecialMarketCompetitions(betPart, specialMarket),
    'competitionName',
  ));
  if (competitionNames.length) return uniq(competitionNames);
  return getSpecialMarketNames(specialMarket, 'competitions', 'competitionName');
};

const getSpecialMarketContext = (betPart) => {
  const specialMarket = getSpecialMarket(betPart);
  if (!specialMarket) return null;

  const events = getSpecialMarketEvents(betPart, specialMarket);
  const competitions = getSpecialMarketCompetitions(betPart, specialMarket);
  const sports = getSpecialMarketSports(betPart, specialMarket);
  const primaryEvent = events[0];
  const primaryCompetition = competitions[0];
  const primarySport = sports[0];
  const competitionNames = getSpecialMarketCompetitionNames(betPart, specialMarket);

  return {
    specialMarket,
    eventId: primaryEvent?.eventId || specialMarket.eventIds?.[0],
    eventIds: compact(map(events, 'eventId')).length
      ? compact(map(events, 'eventId'))
      : (specialMarket.eventIds || []),
    eventName: getSpecialMarketEventName(betPart, specialMarket) || '',
    competitionName: competitionNames[0] || primaryCompetition?.competitionName || '',
    competitionNames,
    competitionIds: compact(map(competitions, 'competitionId')).length
      ? compact(map(competitions, 'competitionId'))
      : (specialMarket.competitionIds || []),
    competitors: primaryEvent?.competitors,
    sportId: primarySport?.sportId || primaryEvent?.sportId || specialMarket.sportIds?.[0],
    sportIds: compact(map(sports, 'sportId')).length
      ? compact(map(sports, 'sportId'))
      : (specialMarket.sportIds || []),
    sport: primarySport || betPart?.huddleContentInfo?.sport,
    isUsaView: primaryEvent?.isUSAView || primaryEvent?.isUsaView,
  };
};

const createColumns = () => ({
  flag: {
    label: '',
    visible: true,
    order: 1,
    filterable: false,
    sortable: false,
    nonResizable: true,
    minWidth: 160,
    maxWidth: 160,
    toggleable: false,
  },
  betId: {
    label: 'Bet ID',
    visible: true,
    order: 2,
    filterable: true,
    sortable: false,
    minWidth: 100,
  },
  competitionName: {
    label: 'Competition',
    visible: true,
    order: 3,
    filterable: true,
    sortable: true,
    minWidth: 150,
    maxWidth: 250,
  },
  eventName: {
    label: 'Event',
    visible: true,
    order: 4,
    filterable: true,
    sortable: false,
    minWidth: 230,
    maxWidth: 300,
  },
  market: {
    label: 'Market',
    visible: true,
    order: 5,
    filterable: true,
    sortable: false,
    minWidth: 170,
    maxWidth: 220,
  },
  selection: {
    label: 'Selection',
    visible: true,
    order: 6,
    filterable: true,
    sortable: false,
    minWidth: 120,
    maxWidth: 220,
  },
  odds: {
    label: 'Odds',
    visible: true,
    order: 7,
    filterable: true,
    sortable: false,
    minWidth: 90,
    maxWidth: 90,
  },
  date: {
    label: 'Date',
    visible: true,
    order: 8,
    inputType: 'date',
    defaultValue: () => formatDate(new Date(), DateTypes.ISO_DATE),
    filterable: true,
    sortable: false,
    minWidth: 140,
    maxWidth: 140,
  },
  time: {
    label: 'Time',
    visible: true,
    order: 9,
    filterable: true,
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  strength: {
    label: 'Strength',
    visible: true,
    order: 10,
    filterable: true,
    sortable: false,
    minWidth: 80,
    maxWidth: 80,
  },
  betOutcome: {
    label: 'Settlement status',
    visible: false,
    order: 10,
    filterable: true,
    sortable: false,
    minWidth: 140,
    maxWidth: 80,
  },
  nickname: {
    label: 'Nickname',
    visible: false,
    order: 11,
    filterable: true,
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  playerAccountId: {
    label: 'Account Id',
    visible: true,
    order: 12,
    filterable: false,
    sortable: false,
    minWidth: 170,
    maxWidth: 220,
  },
  /* name: {
    label: 'Name',
    visible: false,
    order: 11,
    filterable: true,
    sortable: false,
  }, */
  eventId: {
    label: 'Event ID',
    visible: false,
    order: 13,
    filterable: true,
    sortable: false,
  },
  client: {
    label: 'Client',
    visible: true,
    order: 14,
    filterable: true,
    sortable: false,
    minWidth: 80,
    maxWidth: 80,
  },
  stakeFactor: {
    label: 'Stake factor',
    visible: false,
    order: 15,
    filterable: true,
    sortable: false,
  },
  maxBetPercentage: {
    label: '% Max Bet',
    visible: false,
    order: 16,
    filterable: true,
    sortable: false,
  },
  /* autoMove: {
    label: 'Auto move',
    visible: false,
    order: 16,
    filterable: true,
    sortable: false,
  }, */
  stake: {
    label: 'Stake',
    visible: true,
    order: 17,
    filterable: true,
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  freeStake: {
    label: 'Free stake',
    visible: false,
    order: 18,
    filterable: true,
    sortable: false,
    minWidth: 100,
    maxWidth: 100,
  },
  potentialPayout: {
    label: 'Potential payout',
    visible: true,
    order: 19,
    filterable: true,
    sortable: false,
    minWidth: 150,
  },
  /* limit: {
    label: 'Bet limit',
    visible: true,
    order: 18,
    filterable: true,
    sortable: false,
    minWidth: 100,
    maxWidth: 100,
  }, */
});

const formatDateTime = (dateTime) => {
  const formattedDate = format(zonedTimeToUtc(dateTime, 'UTC'), 'MM/dd/yyyy');
  const formattedTime = format(zonedTimeToUtc(dateTime, 'UTC'), 'hh:mm a');

  return {
    formattedDate,
    formattedTime,
  };
};

export const formatBetsData = (bets) => {
  const formatedBets = map(bets, (bet) => {
    const actionTime = bet.betActions?.[0]?.actionTime;
    const betParts = bet.betPartsByBetId.nodes;
    const betPartPrice = {
      decimalValue: bet.inferredDecimalPrice,
      probability: bet.inferredBetProbability,
      bias: 0,
      originalFormattedValue: bet.inferredOriginalFormattedPrice,
    };
    const isMultibet = betParts.length > 1;
    const partWithSpecialMarket = find(betParts, (betPart) => betPart.specialMarket != null);
    const specialMarket = partWithSpecialMarket?.specialMarket;
    const isSpecialMarket = specialMarket != null;
    const numberOfDifferentEvents = uniqBy(betParts, 'event.eventName')?.length;
    const hasDifferentEvents = isMultibet && numberOfDifferentEvents !== 1;
    const marketCode = betParts[0]?.marketType?.marketCode;
    const selection = betParts[0]?.selection;
    const marketParams = betParts[0]?.marketType?.params;
    const isUsaView = betParts[0]?.event?.isUsaView;
    const eventNameFormatted = formatEventName(betParts[0]?.event, isUsaView);
    const specialEventName = isSpecialMarket
      ? getSpecialMarketEventName(partWithSpecialMarket, specialMarket)
      : null;
    const eventName = specialEventName
      || (hasDifferentEvents ? numberOfDifferentEvents : eventNameFormatted);
    const competitionNames = isSpecialMarket
      ? getSpecialMarketCompetitionNames(partWithSpecialMarket, specialMarket)
      : [];
    const competitionName = competitionNames[0]
      || betParts[0]?.event?.competition?.competitionName
      || '';
    const { formattedDate, formattedTime } = formatDateTime(bet.acceptanceTime || actionTime);
    const betInfo = {
      date: formattedDate,
      time: formattedTime,
      acceptedBy: bet.betActions?.[0]?.agentId,
      client: bet.operatorId,
    };
    const { betActions } = bet;

    const betPartGroups = bet.betPartGroups || [];
    let market = {};
    if (isSpecialMarket) market = specialMarket?.specialName || marketCode;
    else if (bet.betType === 'MULTIPLE') market = 'Parlay';
    else if (bet.betType === 'SPECIAL' && betPartGroups.length === 1 && betPartGroups?.[0]?.type === 'SAME_GAME_PARLAY') market = 'SGP';
    else if (bet.betType === 'SPECIAL' && betPartGroups.length > 1) market = 'SGP+';
    else market = marketCode;

    const isSharpCustomer = some(bet.accountInfo?.profilingLabels, { label: 'SHARP' });
    return ({
      ...betInfo,
      betId: bet.betId,
      operatorId: bet.operatorId,
      betPartGroups,
      stake: parseFloat(bet.betStake.amount / 100).toFixed(2),
      freeStake: parseFloat(bet.betStake.freeBetAmount / 100).toFixed(2),
      stakeFactor: parseFloat(bet.accountInfo?.stakeFactor * 100).toFixed(2),
      maxBetPercentage: parseFloat(bet.accountInfo?.maxBetPercentage * 100).toFixed(2),
      potentialPayout: parseFloat(bet.betPayout.max / 100).toFixed(2),
      betPartsCount: betParts.length,
      multibet: isMultibet,
      isSpecialMarket,
      eventId: betParts[0]?.event?.eventId,
      eventName,
      hasDifferentEvents,
      competitionName,
      competitionNames,
      competitors: betParts[0]?.event?.competitors,
      sportId: betParts[0]?.event?.sportId,
      sport: betParts[0]?.event?.sport,
      isUsaView: betParts[0]?.event?.isUsaView,
      isFlagged: bet.isFlagged,
      flagType: bet.flagType,
      hasExternalParts: bet.hasExternalParts,
      odds: betPartPrice,
      market,
      marketParams,
      marketSummary: betParts[0]?.marketSummary,
      limit: parseFloat(betParts[0]?.limit / 100).toFixed(2),
      selection,
      playerAccountId: bet.playerAccountId,
      strength: bet.accountInfo?.strength,
      nickname: bet.accountInfo?.nickname,
      betOutcome: bet.betOutcome,
      isSharpCustomer,
      betParts: map(betParts, (betPart) => {
        const partMarketCode = betPart.marketType?.marketCode;
        const partSelection = betPart.selection;
        const partMarketParams = betPart.marketType?.params;
        const partIsUsaView = betPart.event?.isUsaView;
        const partEventName = formatEventName(betPart.event, partIsUsaView);
        const partIsSpecialMarket = betPart.specialMarket != null;
        const partSpecialEventName = partIsSpecialMarket
          ? getSpecialMarketEventName(betPart, betPart.specialMarket)
          : null;
        const partCompetitionNames = partIsSpecialMarket
          ? getSpecialMarketCompetitionNames(betPart, betPart.specialMarket)
          : [];
        return ({
          ...betInfo,
          betId: betPart.betId,
          eventId: betPart.event?.eventId,
          eventName: partSpecialEventName || partEventName,
          competitionName: partCompetitionNames[0]
            || betPart.event?.competition?.competitionName
            || '',
          competitionNames: partCompetitionNames,
          competitors: betPart.event?.competitors,
          sportId: betPart.event?.sportId,
          sport: betPart.event?.sport,
          isUsaView: betPart.event?.isUsaView,
          market: partIsSpecialMarket
            ? (betPart.specialMarket?.specialName || partMarketCode)
            : partMarketCode,
          marketParams: partMarketParams,
          marketSummary: betPart.marketSummary,
          limit: parseFloat(betPart.limit / 100).toFixed(2),
          selection: partSelection,
          odds: betPart.price,
          isBetPart: true,
          isSpecialMarket: partIsSpecialMarket,
          playerAccountId: bet.playerAccountId,
          betOutcome: betPart.betPartOutcome,
        });
      }),
      betActions,
    });
  });

  return formatedBets;
};

export const formatBetData = (bet) => {
  const betParts = bet.parts;
  const betPartPrice = {
    decimalValue: bet.inferredDecimalPrice,
    probability: bet.inferredBetProbability,
    bias: 0,
    originalFormattedValue: bet.inferredOriginalFormattedPrice,
  };
  const isMultibet = betParts.length > 1;
  const partWithSpecialMarket = find(betParts, (betPart) => getSpecialMarket(betPart) != null);
  const specialMarketContext = getSpecialMarketContext(partWithSpecialMarket);
  const specialMarket = specialMarketContext?.specialMarket;
  const isSpecialMarket = specialMarket != null;
  const numberOfDifferentEvents = uniqBy(betParts, 'huddleContentInfo.event.eventName')?.length;
  const hasDifferentEvents = isMultibet && numberOfDifferentEvents !== 1;
  const marketCode = betParts[0]?.huddleContentInfo?.market?.marketType?.marketCode;
  const selection = getBetPartSelection(betParts[0]);
  const marketParams = betParts[0]?.huddleContentInfo?.market?.marketType?.params;
  const isUsaView = betParts[0]?.huddleContentInfo?.event?.isUSAView
    || specialMarketContext?.isUsaView;
  const eventNameFormatted = formatEventName(betParts[0]?.huddleContentInfo?.event, isUsaView);
  const eventName = specialMarketContext?.eventName
    || (hasDifferentEvents ? numberOfDifferentEvents : eventNameFormatted);
  const competitionNames = specialMarketContext?.competitionNames || [];
  const competitionName = competitionNames[0]
    || betParts[0]?.huddleContentInfo?.competition?.competitionName
    || '';
  const { formattedDate, formattedTime } = formatDateTime(bet.acceptanceTime);
  const betInfo = {
    date: formattedDate,
    time: formattedTime,
    acceptedBy: '-',
    client: bet.operatorId,
  };
  const { betActions } = bet;

  const betPartGroups = bet.partGroups || [];
  let market = {};
  if (isSpecialMarket) market = specialMarket?.specialName || marketCode;
  else if (bet.betType === 'MULTIPLE') market = 'Parlay';
  else if (bet.betType === 'SPECIAL' && betPartGroups.length === 1 && betPartGroups?.[0]?.type === 'SAME_GAME_PARLAY') market = 'SGP';
  else if (bet.betType === 'SPECIAL' && betPartGroups.length > 1) market = 'SGP+';
  else market = marketCode;

  const isSharpCustomer = some(bet.accountInfo?.profilingLabels, { label: 'SHARP' });
  return ({
    ...betInfo,
    betId: bet.id,
    operatorId: bet.operatorId,
    betPartGroups,
    stake: parseFloat(bet.stake.amount / 100).toFixed(2),
    freeStake: parseFloat(bet.stake.freeBetAmount / 100).toFixed(2),
    potentialPayout: parseFloat(bet.payout.max / 100).toFixed(2),
    betPartsCount: betParts.length,
    multibet: isMultibet,
    isSpecialMarket,
    eventId: betParts[0]?.huddleContentInfo?.event?.eventId || specialMarketContext?.eventId,
    eventIds: specialMarketContext?.eventIds || [],
    eventName,
    hasDifferentEvents,
    competitionName,
    competitionNames,
    competitors: betParts[0]?.huddleContentInfo?.event?.competitors
      || specialMarketContext?.competitors,
    competitionIds: specialMarketContext?.competitionIds || [],
    sportId: betParts[0]?.huddleContentInfo?.event?.sportId || specialMarketContext?.sportId,
    sportIds: specialMarketContext?.sportIds || [],
    sport: betParts[0]?.huddleContentInfo?.sport || specialMarketContext?.sport,
    isUsaView,
    hasExternalParts: bet.hasExternalParts,
    odds: betPartPrice,
    market,
    marketParams,
    marketSummary: betParts[0]?.huddleContentInfo?.market?.marketSummary,
    limit: betParts[0]?.huddleContentInfo?.market?.limit,
    selection,
    playerAccountId: bet.externalPlayerId,
    strength: bet.accountInfo?.strength,
    nickname: bet.accountInfo?.nickname,
    stakeFactor: parseFloat(bet.accountInfo?.stakeFactor * 100).toFixed(2),
    maxBetPercentage: parseFloat(bet.accountInfo?.maxBetPercentage * 100).toFixed(2),
    highlightedRow: true, // highlight new bets so that traders know new bets arrived after last time they looked at bet-ticker
    betOutcome: bet.betOutcome,
    isSharpCustomer,
    betParts: map(betParts, (betPart) => {
      const partMarketCode = betPart.huddleContentInfo?.market?.marketType?.marketCode;
      const partSelection = getBetPartSelection(betPart);
      const partMarketParams = betPart.huddleContentInfo?.market?.marketType?.params;
      const partSpecialMarketContext = getSpecialMarketContext(betPart);
      const partIsSpecialMarket = partSpecialMarketContext != null;
      const partIsUsaView = betPart.huddleContentInfo?.event?.isUSAView
        || partSpecialMarketContext?.isUsaView;
      const partEventName = formatEventName(betPart.huddleContentInfo?.event, partIsUsaView);
      return ({
        ...betInfo,
        betId: betPart.id,
        eventId: betPart.huddleContentInfo?.event?.eventId || partSpecialMarketContext?.eventId,
        eventIds: partSpecialMarketContext?.eventIds || [],
        eventName: partSpecialMarketContext?.eventName || partEventName,
        competitionName: partSpecialMarketContext?.competitionName
          || betPart.huddleContentInfo?.competition?.competitionName
          || '',
        competitionNames: partSpecialMarketContext?.competitionNames || [],
        competitors: betPart.huddleContentInfo?.event?.competitors
          || partSpecialMarketContext?.competitors,
        competitionIds: partSpecialMarketContext?.competitionIds || [],
        sportId: betPart.huddleContentInfo?.event?.sportId || partSpecialMarketContext?.sportId,
        sportIds: partSpecialMarketContext?.sportIds || [],
        sport: betPart.huddleContentInfo?.sport || partSpecialMarketContext?.sport,
        isUsaView: partIsUsaView,
        market: partIsSpecialMarket
          ? (partSpecialMarketContext.specialMarket?.specialName || partMarketCode)
          : partMarketCode,
        marketParams: partMarketParams,
        marketSummary: betPart.huddleContentInfo?.market?.marketSummary,
        limit: betPart.huddleContentInfo?.market?.limit,
        selection: partSelection,
        odds: partSelection?.price,
        isBetPart: true,
        isSpecialMarket: partIsSpecialMarket,
        playerAccountId: bet.externalPlayerId,
        betOutcome: betPart.betPartOutcome,
      });
    }),
    betActions,
  });
};

export default {
  createColumns,
};
